import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Upload,
  Download,
  Plus,
  Edit,
  Trash2,
  Star,
  MessageSquare,
  Search,
  User,
  AlertCircle,
  Eye,
  Save,
  RefreshCw,
  X,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import trainingService from '../../../api/services/trainingService';
import { useNotification } from '../../../components/common/NotificationProvider';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import { API_BASE_URL } from '../../../api/config';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { formatDate } from '../../../utils/stringUtils';

const AttendanceFeedback = () => {
  // Notification hooks
  const { showSuccess, showError, showWarning } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryClient = useQueryClient();

  // State for attendance management
  const [selectedSession, setSelectedSession] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [attendanceNotes, setAttendanceNotes] = useState({});

  // Pagination State — initialized from URL
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);

  // State for filters and search — initialized from URL
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
  });
  // Group selection and export state
  const [selectedRows, setSelectedRows] = useState(new Map());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [modalSortConfig, setModalSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [formSortConfig, setFormSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Bulk Delete State
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);


  const [showImportModal, setShowImportModal] = useState(false);
  const [showViewAttendanceModal, setShowViewAttendanceModal] = useState(false);
  const [viewAttendanceDetails, setViewAttendanceDetails] = useState(null);
  const [isViewAttendanceLoading, setIsViewAttendanceLoading] = useState(false);
  const [viewAttendanceError, setViewAttendanceError] = useState('');
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [sessionParticipantsCache, setSessionParticipantsCache] = useState({});
  const [importFile, setImportFile] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [recordPendingDeletion, setRecordPendingDeletion] = useState(null);
  const [isDeletingAttendance, setIsDeletingAttendance] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const baseExportFilters = useMemo(() => ({
    search: debouncedSearchTerm,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  }), [debouncedSearchTerm, dateRange]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filtersToUse = overrideFilters ?? baseExportFilters;
      const payload = {
        module: "training-attendance",
        scope,
        format,
        filters: filtersToUse,
        page: overridePage ?? currentPage,
        limit: overrideLimit ?? itemsPerPage,
        sortBy: sortConfig.key || 'scheduledDate',
        sortOrder: sortConfig.direction || 'desc',
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Export failed");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `training-attendance-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, currentPage, itemsPerPage, sortConfig]
  );

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Sync filter/pagination state to URL search params (persists on refresh)
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'startDate', 'endDate', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));

      return params;
    }, { replace: true });
  }, [debouncedSearchTerm, dateRange, currentPage, itemsPerPage, setSearchParams]);

  const formatAttendanceParticipant = (record, index = 0) => {
    const employee = record.employee || {};
    const firstLast = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
    const name =
      employee.name ||
      (firstLast && firstLast !== '' ? firstLast : null) ||
      record.employeeName ||
      record.userName ||
      record.email ||
      'Unknown';
    const email = employee.email || record.employeeEmail || record.email || '';
    const department = employee.department || record.department || '';
    const userId =
      record.userId ||
      employee.user ||
      employee.userId ||
      employee._id ||
      record.employee;
    const status = (record.status || 'present').toLowerCase();

    return {
      id: userId ? String(userId) : `${record._id || record.id || 'participant'}-${index}`,
      userId: userId ? String(userId) : null,
      attendanceId: record._id || record.id || null,
      name,
      email,
      department,
      status,
      notes: record.notes || ''
    };
  };

  // Fetch sessions list with pagination and search
  const {
    data: sessionsQueryResult,
    isLoading: isSessionsLoading,
    isError: isSessionsError,
    error: sessionsError
  } = useQuery({
    queryKey: ['sessions', debouncedSearchTerm, dateRange, currentPage, itemsPerPage],
    queryFn: () => trainingService.getSessions({
      search: debouncedSearchTerm,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
      page: currentPage,
      limit: itemsPerPage,
      sortBy: 'scheduledDate',
      sortOrder: 'desc'
    }),
    keepPreviousData: true
  });

  const paginatedSessions = useMemo(() => {
    return sessionsQueryResult?.data?.sessions || sessionsQueryResult?.data || [];
  }, [sessionsQueryResult]);

  // Fetch all sessions for the "Select Session" dropdown (not paginated)
  const { data: dropdownSessionsData } = useQuery({
    queryKey: ['sessions', 'dropdown'],
    queryFn: () => trainingService.getSessions({
      limit: 1000,
      sortBy: 'scheduledDate',
      sortOrder: 'desc',
      status: 'scheduled,in_progress'
    }),
    staleTime: 0,
  });

  const dropdownSessions = useMemo(() => {
    return dropdownSessionsData?.data?.sessions || dropdownSessionsData?.data || [];
  }, [dropdownSessionsData]);

  // Fetch attendance records - DISABLED on initial load, only triggered by specific needs
  const {
    data: attendanceQueryResult,
    isLoading,
    isError,
    error: queryError
  } = useQuery({
    queryKey: ['attendance', debouncedSearchTerm, currentPage, itemsPerPage],
    queryFn: () => trainingService.getAttendance({
      search: debouncedSearchTerm,
      page: currentPage,
      limit: itemsPerPage,
      sortBy: 'checkInTime',
      sortOrder: 'desc'
    }),
    keepPreviousData: true,
    enabled: false // Disable direct fetching on load as per request
  });

  // Use sessions as the primary data source for the main table
  const attendanceData = useMemo(() => {
    return paginatedSessions.map(session => ({
      id: session.id || session._id,
      sessionId: session.id || session._id,
      sessionTitle: session.title,
      date: session.scheduledDate || session.startTime,
      attendance: session.attendanceCounts || {},
      records: [] // Full records can be fetched on demand
    }));
  }, [paginatedSessions]);

  const pagination = useMemo(() => {
    const rawPagination = sessionsQueryResult?.data?.pagination || sessionsQueryResult?.pagination;
    return {
      currentPage: rawPagination?.currentPage || 1,
      totalPages: rawPagination?.totalPages || 1,
      totalRecords: rawPagination?.totalSessions || rawPagination?.totalRecords || 0,
      hasNextPage: rawPagination?.hasNextPage || false,
      hasPrevPage: rawPagination?.hasPrevPage || false,
      limit: rawPagination?.limit || itemsPerPage
    };
  }, [sessionsQueryResult, itemsPerPage]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAttendanceData = useMemo(() => {
    const sortableItems = [...attendanceData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'sessionTitle':
            aValue = (a.sessionTitle || '').toLowerCase();
            bValue = (b.sessionTitle || '').toLowerCase();
            break;
          case 'date':
            aValue = a.date ? new Date(a.date) : new Date(0);
            bValue = b.date ? new Date(b.date) : new Date(0);
            break;
          case 'present':
            aValue = (a.attendance?.present !== undefined ? a.attendance.present : Object.values(a.attendance || {}).filter(s => s === 'present').length) || 0;
            bValue = (b.attendance?.present !== undefined ? b.attendance.present : Object.values(b.attendance || {}).filter(s => s === 'present').length) || 0;
            break;
          case 'absent':
            aValue = (a.attendance?.absent !== undefined ? a.attendance.absent : Object.values(a.attendance || {}).filter(s => s === 'absent').length) || 0;
            bValue = (b.attendance?.absent !== undefined ? b.attendance.absent : Object.values(b.attendance || {}).filter(s => s === 'absent').length) || 0;
            break;
          case 'late':
            aValue = (a.attendance?.late !== undefined ? a.attendance.late : Object.values(a.attendance || {}).filter(s => s === 'late').length) || 0;
            bValue = (b.attendance?.late !== undefined ? b.attendance.late : Object.values(b.attendance || {}).filter(s => s === 'late').length) || 0;
            break;
          default:
            aValue = a[sortConfig.key] || '';
            bValue = b[sortConfig.key] || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [attendanceData, sortConfig]);

  const handleModalSort = (key) => {
    let direction = 'asc';
    if (modalSortConfig.key === key && modalSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setModalSortConfig({ key, direction });
  };


  // Sessions loaded from API in loadAttendanceData

  // Participants will be loaded from API
  const [participants, setParticipants] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [invitees, setInvitees] = useState([]);
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const [inviteeSearchTerm, setInviteeSearchTerm] = useState('');

  const handleFormSort = (key) => {
    let direction = 'asc';
    if (formSortConfig.key === key && formSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setFormSortConfig({ key, direction });
  };

  const sortedInvitees = useMemo(() => {
    let result = invitees.filter(invitee => {
      if (!inviteeSearchTerm) return true;
      const term = inviteeSearchTerm.toLowerCase();
      return (
        (invitee.name && invitee.name.toLowerCase().includes(term)) ||
        (invitee.email && invitee.email.toLowerCase().includes(term)) ||
        (invitee.department && invitee.department.toLowerCase().includes(term))
      );
    });

    if (formSortConfig.key) {
      result.sort((a, b) => {
        let aValue = (a[formSortConfig.key] || '').toString().toLowerCase();
        let bValue = (b[formSortConfig.key] || '').toString().toLowerCase();
        if (aValue < bValue) return formSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return formSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [invitees, inviteeSearchTerm, formSortConfig]);

  const [isAttendanceFormOpen, setIsAttendanceFormOpen] = useState(false);
  const [isEditingAttendance, setIsEditingAttendance] = useState(false);

  const resolveUserId = (entity) => {
    if (!entity) return null;
    const candidates = [
      entity.userId,
      entity.user?.id,
      entity.user?._id,
      entity.user,
      entity.employeeUser?.id,
      entity.employeeUser?._id,
      entity.employeeUser,
      entity.employee?.user?.id,
      entity.employee?.user?._id,
      entity.employee?.user,
      entity.employeeData?.userId,
      entity.employeeData?._id,
      entity.employeeId,
      entity.employee?._id,
      entity.employee,
      entity._id,
      entity.id
    ];
    const found = candidates.find(Boolean);
    return found ? String(found) : null;
  };

  const resolveFallbackId = (entity, role, index) => {
    if (!entity) return `${role}-${index}-${Date.now()}`;
    const candidates = [
      entity.employee?.employeeId,
      entity.employee?._id,
      entity.employee,
      entity.employeeId,
      entity._id,
      entity.id
    ];
    const found = candidates.find(Boolean);
    return found ? String(found) : `${role}-${index}-${Date.now()}`;
  };

  const uniqueById = (people = []) => {
    const map = new Map();
    people.forEach(person => {
      if (person?.id && !map.has(person.id)) {
        map.set(person.id, person);
      }
    });
    return Array.from(map.values());
  };

  const normalizeSessionParticipants = (session) => {
    if (!session) {
      return { trainers: [], invitees: [], combined: [] };
    }

    const trainers = uniqueById((session.instructor || []).map((instructor, index) => {
      const userId = resolveUserId(instructor);
      const displayId = userId || resolveFallbackId(instructor, 'trainer', index);
      if (!displayId) {
        return null;
      }
      const sourceEmployee = instructor.employee || instructor.employeeData || {};
      const department = instructor.department || sourceEmployee.department || '';
      const email = instructor.email || sourceEmployee.email || '';
      const name = instructor.name ||
        (instructor.firstName && instructor.lastName
          ? `${instructor.firstName} ${instructor.lastName}`
          : instructor.firstName || instructor.lastName || sourceEmployee.name || 'Unknown Trainer');
      return {
        id: displayId,
        userId: userId ? String(userId) : null,
        name,
        email,
        department,
        role: 'trainer'
      };
    }).filter(Boolean));

    const inviteesRaw = (session.enrolledParticipants || []).map((participant, index) => {
      let userId = resolveUserId(participant);
      let name;
      let email;
      let department;

      if (participant.employeeData) {
        name = participant.employeeData.name || 'Unknown Participant';
        email = participant.employeeData.email || '';
        department = participant.employeeData.department || '';
      } else if (participant.employee && typeof participant.employee === 'object' && (participant.employee.firstName || participant.employee.lastName || participant.employee.email)) {
        name = `${participant.employee.firstName || ''} ${participant.employee.lastName || ''}`.trim() || participant.employee.email || 'Unknown Participant';
        email = participant.employee.email || '';
        department = participant.employee.department || '';
      } else if (participant.employee) {
        name = 'Unknown Participant';
        email = '';
        department = '';
      } else {
        const employee = participant.user || participant;
        name = employee?.name ||
          employee?.fullName ||
          `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() ||
          employee?.email ||
          participant.email ||
          'Unknown Participant';
        email = employee?.email || participant.email || '';
        department = employee?.department || participant.department || '';
      }

      if (!userId) {
        userId = resolveUserId(participant.employee) || resolveUserId(participant.user) || resolveUserId(participant.employeeData);
      }

      const displayId = userId || resolveFallbackId(participant, 'invitee', index);
      if (!displayId) {
        return null;
      }

      return {
        id: displayId,
        userId: userId ? String(userId) : null,
        name,
        email,
        department,
        role: 'invitee'
      };
    }).filter(Boolean);

    const trainerIds = new Set(trainers.map(t => String(t.userId || t.id)));
    const invitees = uniqueById(inviteesRaw).filter(invitee => !trainerIds.has(String(invitee.userId || invitee.id)));
    const combined = uniqueById([...trainers, ...invitees]);

    return { trainers, invitees, combined };
  };

  const buildParticipantDirectory = (people = []) => {
    const directory = {};
    people.forEach(person => {
      const entry = {
        name: person.name,
        email: person.email || '',
        department: person.department || '',
        role: person.role
      };
      if (person.userId) {
        directory[String(person.userId)] = entry;
      }
      if (person.id) {
        directory[String(person.id)] = entry;
      }
    });
    return directory;
  };

  const buildParticipantFallbackMapFromRecords = (records = []) => {
    const fallback = {};
    records.forEach((record, index) => {
      const employeeEntity = record.employee || record.employeeData || record.user || {};
      const firstName = employeeEntity.firstName || record.firstName;
      const lastName = employeeEntity.lastName || record.lastName;
      const combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const name =
        record.employeeName ||
        employeeEntity.name ||
        combinedName ||
        record.userName ||
        employeeEntity.email ||
        record.email ||
        `Participant ${index + 1}`;
      const email = employeeEntity.email || record.employeeEmail || record.email || '';
      const department = employeeEntity.department || record.department || '';
      const candidateIds = [
        record.userId,
        employeeEntity.user,
        employeeEntity.userId,
        employeeEntity._id,
        record.employeeId,
        record.employee,
        record.id,
        record._id
      ];
      candidateIds.forEach(candidate => {
        if (!candidate) return;
        fallback[String(candidate)] = { name, email, department };
      });
    });
    return fallback;
  };

  const enrichParticipantsWithFallbacks = (participantsList = [], fallbackSources = []) => {
    return participantsList.map((participant, index) => {
      let fallbackEntry = null;
      for (const source of fallbackSources) {
        if (!source || fallbackEntry) continue;
        if (participant.userId && source[String(participant.userId)]) {
          fallbackEntry = source[String(participant.userId)];
        } else if (participant.id && source[String(participant.id)]) {
          fallbackEntry = source[String(participant.id)];
        }
      }

      return {
        ...participant,
        name: fallbackEntry?.name || participant.name || `Participant ${index + 1}`,
        email: fallbackEntry?.email || participant.email || '',
        department: fallbackEntry?.department || participant.department || ''
      };
    });
  };

  const getSessionParticipantsDirectory = async (sessionId) => {
    if (!sessionId) {
      return {};
    }
    if (sessionParticipantsCache[sessionId]) {
      return sessionParticipantsCache[sessionId];
    }

    let session = dropdownSessions.find(s => (s.id || s._id) === sessionId);
    if (!session || (!session.enrolledParticipants && !session.instructor)) {
      try {
        const sessionResult = await trainingService.getSessionById(sessionId);
        if (sessionResult.success && sessionResult.data) {
          session = sessionResult.data;
        }
      } catch (error) {
      }
    }

    if (!session) {
      return {};
    }

    const normalized = normalizeSessionParticipants(session);
    const directory = buildParticipantDirectory(normalized.combined);
    setSessionParticipantsCache(prev => ({
      ...prev,
      [sessionId]: directory
    }));
    return directory;
  };

  const clearAttendanceFormState = () => {
    setSelectedSession('');
    setSelectedSessionData(null);
    setTrainers([]);
    setInvitees([]);
    setParticipants([]);
    setInviteeSearchTerm('');
    setAttendanceStatus({});
    setAttendanceNotes({});
    setSelectedAttendanceRecord(null);
  };

  const handleCancelAttendanceForm = () => {
    clearAttendanceFormState();
    setIsAttendanceFormOpen(false);
    setIsEditingAttendance(false);
  };

  // Load session details (trainers and invitees) when a session is selected
  useEffect(() => {
    const loadSessionDetails = async () => {
      if (!selectedSession) {
        setTrainers([]);
        setInvitees([]);
        setParticipants([]);
        setSelectedSessionData(null);
        setInviteeSearchTerm('');
        setAttendanceStatus({});
        setAttendanceNotes({});
        return;
      }
      try {
        // Get session from the sessions list first (it should have enrolledParticipants)
        let session = dropdownSessions.find(s => (s.id || s._id) === selectedSession);

        // If not found in list or missing data, try to fetch it directly
        if (!session || !session.enrolledParticipants) {
          try {
            const sessionResult = await trainingService.getSessionById(selectedSession);
            if (sessionResult.success && sessionResult.data) {
              session = sessionResult.data;
            }
          } catch (e) {
          }
        }

        if (session) {
          setSelectedSessionData(session);

          const { trainers: sessionTrainers, invitees: sessionInvitees, combined: allPeople } = normalizeSessionParticipants(session);
          setTrainers(sessionTrainers);
          setInvitees(sessionInvitees);
          setInviteeSearchTerm('');
          setSessionParticipantsCache(prev => ({
            ...prev,
            [selectedSession]: buildParticipantDirectory(allPeople)
          }));

          let attendanceState = {};
          let notesState = {};
          let foundExistingAttendance = false;

          // Check if attendance already exists for this session
          try {
            const existingAttendanceResult = await trainingService.getAttendance({
              sessionId: selectedSession,
              page: 1,
              limit: 1000
            });

            let existingAttendanceMap = {};
            let existingNotesMap = {};

            if (existingAttendanceResult.success) {
              const existingRecords = existingAttendanceResult.data?.attendanceRecords ||
                existingAttendanceResult.data?.data ||
                (Array.isArray(existingAttendanceResult.data) ? existingAttendanceResult.data : []);

              // Map existing attendance by user ID returned from API
              existingRecords.forEach(record => {
                const resolvedUserId =
                  record.userId ||
                  record.employee?.user ||
                  record.employee?.user?._id ||
                  record.employeeUser ||
                  record.employee?.userId;
                if (resolvedUserId) {
                  const key = String(resolvedUserId);
                  existingAttendanceMap[key] = record.status || 'present';
                  if (record.notes) {
                    existingNotesMap[key] = record.notes;
                  }
                }
              });

              // If attendance exists, load their status
              if (Object.keys(existingAttendanceMap).length > 0) {
                attendanceState = { ...existingAttendanceMap };
                notesState = existingNotesMap;
                foundExistingAttendance = true;
              } else {
                attendanceState = {};
                notesState = {};
              }
            } else {
              attendanceState = {};
              notesState = {};
            }
          } catch (e) {
            attendanceState = {};
            notesState = {};
          }

          const normalizedAttendance = {};
          const normalizedNotes = {};
          allPeople.forEach(person => {
            if (person.userId && attendanceState[person.userId] !== undefined) {
              normalizedAttendance[person.id] = attendanceState[person.userId];
            }
            if (person.userId && notesState[person.userId]) {
              normalizedNotes[person.id] = notesState[person.userId];
            }
          });

          setParticipants(allPeople);
          setAttendanceStatus(normalizedAttendance);
          setAttendanceNotes(normalizedNotes);
          if (!foundExistingAttendance) {
            setSelectedAttendanceRecord(null);
          }
        } else {
          setTrainers([]);
          setInvitees([]);
          setParticipants([]);
        }
      } catch (e) {
        setTrainers([]);
        setInvitees([]);
        setParticipants([]);
      }
    };
    loadSessionDetails();
  }, [selectedSession, dropdownSessions]);


  // Handle attendance status change
  const handleAttendanceChange = (participantId, status) => {
    setAttendanceStatus(prev => ({
      ...prev,
      [participantId]: status
    }));
  };

  // Handle attendance notes
  const handleNotesChange = (participantId, notes) => {
    setAttendanceNotes(prev => ({
      ...prev,
      [participantId]: notes
    }));
  };

  // Bulk Mark Attendance Mutation
  const markAttendanceMutation = useMutation({
    mutationFn: (data) => trainingService.bulkMarkAttendance(data),
    onSuccess: () => {
      showSuccess('Attendance saved successfully!');
      clearAttendanceFormState();
      setIsAttendanceFormOpen(false);
      setIsEditingAttendance(false);
      queryClient.invalidateQueries(['attendance']);
    },
    onError: (err) => {
      showError(err.message || 'Failed to save attendance');
    }
  });

  // Save attendance handler
  const handleSaveAttendance = async () => {
    if (participants.length === 0) {
      showWarning('No participants available to mark attendance');
      return;
    }

    const missingStatus = participants.filter(person => attendanceStatus[person.id] === undefined);
    if (missingStatus.length > 0) {
      showWarning('Please mark attendance status for all participants');
      return;
    }

    const participantsMissingUser = participants.filter(person => !person.userId);
    if (participantsMissingUser.length > 0) {
      showWarning('Some participants are missing linked user accounts. Please update their profiles before marking attendance.');
      return;
    }

    const attendanceData = {
      sessionId: selectedSession,
      attendanceData: participants.map(person => {
        const status = attendanceStatus[person.id];
        return {
          userId: String(person.userId),
          status,
          notes: attendanceNotes[person.id] || '',
          checkInTime: (status === 'present' || status === 'late') ? new Date().toISOString() : null
        };
      })
    };

    markAttendanceMutation.mutate(attendanceData);
  };

  // View attendance details
  const handleViewAttendance = async (record) => {
    setSelectedAttendanceRecord(record);
    setShowViewAttendanceModal(true);
    setViewAttendanceDetails(null);
    setViewAttendanceError('');
    setIsViewAttendanceLoading(true);

    const sessionId = record.sessionId || record.session?._id || record.session;
    const baseFallbackParticipants = (record.records || []).map((rec, idx) => formatAttendanceParticipant(rec, idx));
    let sessionDirectory = sessionParticipantsCache[sessionId] || {};

    try {
      sessionDirectory = await getSessionParticipantsDirectory(sessionId);
      const response = await trainingService.getAttendance({
        sessionId,
        page: 1,
        limit: 1000,
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      });

      let rawRecords = [];
      if (response.success) {
        if (Array.isArray(response.data)) {
          rawRecords = response.data;
        } else if (Array.isArray(response.data?.attendanceRecords)) {
          rawRecords = response.data.attendanceRecords;
        } else if (Array.isArray(response.data?.data)) {
          rawRecords = response.data.data;
        }
      }

      const apiFallbackMap = buildParticipantFallbackMapFromRecords(rawRecords);
      const groupedFallbackMap = buildParticipantFallbackMapFromRecords(record.records || []);
      const fallbackSources = [sessionDirectory, apiFallbackMap, groupedFallbackMap];
      const formattedParticipants = rawRecords.map((rec, index) => formatAttendanceParticipant(rec, index));
      const enrichedFormatted = enrichParticipantsWithFallbacks(formattedParticipants, fallbackSources);
      const enrichedFallback = enrichParticipantsWithFallbacks(baseFallbackParticipants, fallbackSources);
      const participantsForDisplay = enrichedFormatted.length > 0 ? enrichedFormatted : enrichedFallback;

      const presentCount = participantsForDisplay.filter(p => p.status === 'present').length;
      const absentCount = participantsForDisplay.filter(p => p.status === 'absent').length;
      const lateCount = participantsForDisplay.filter(p => p.status === 'late').length;

      setViewAttendanceDetails({
        sessionId,
        sessionTitle: record.sessionTitle || record.session?.title || 'Unknown Session',
        date: record.date || record.records?.[0]?.checkInTime || record.records?.[0]?.createdAt || rawRecords[0]?.checkInTime || rawRecords[0]?.createdAt || null,
        counts: { present: presentCount, absent: absentCount, late: lateCount },
        participants: participantsForDisplay
      });
    } catch (error) {
      setViewAttendanceError('Unable to fetch the latest attendance details. Showing cached data.');
      const groupedFallbackMap = buildParticipantFallbackMapFromRecords(record.records || []);
      const fallbackSources = [sessionDirectory, groupedFallbackMap];
      const fallbackParticipants = enrichParticipantsWithFallbacks(baseFallbackParticipants, fallbackSources);
      setViewAttendanceDetails({
        sessionId,
        sessionTitle: record.sessionTitle || record.session?.title || 'Unknown Session',
        date: record.date || record.records?.[0]?.checkInTime || record.records?.[0]?.createdAt || null,
        counts: {
          present: fallbackParticipants.filter(p => p.status === 'present').length,
          absent: fallbackParticipants.filter(p => p.status === 'absent').length,
          late: fallbackParticipants.filter(p => p.status === 'late').length
        },
        participants: fallbackParticipants
      });
    } finally {
      setIsViewAttendanceLoading(false);
    }
  };

  // Edit attendance - opens modal with session selected and loads existing attendance
  const handleEditAttendance = async (record) => {
    setSelectedAttendanceRecord(record);
    setSelectedSession(record.sessionId);
    setIsEditingAttendance(true);
    setIsAttendanceFormOpen(true);
    // The useEffect will load session details and existing attendance when selectedSession changes
  };

  // Update attendance - uses same mutation
  const handleUpdateAttendance = async () => {
    if (participants.length === 0) {
      showWarning('No participants to update');
      return;
    }

    const missingStatus = participants.filter(p => attendanceStatus[p.id] === undefined);
    if (missingStatus.length > 0) {
      showWarning('Please mark attendance status for all selected participants');
      return;
    }

    const participantsMissingUser = participants.filter(person => !person.userId);
    if (participantsMissingUser.length > 0) {
      showWarning('Some participants are missing linked user accounts. Please update their profiles before updating attendance.');
      return;
    }

    const attendanceData = {
      sessionId: selectedSession,
      attendanceData: participants.map(person => {
        const status = attendanceStatus[person.id];
        return {
          userId: String(person.userId),
          status,
          notes: attendanceNotes[person.id] || '',
          checkInTime: (status === 'present' || status === 'late') ? new Date().toISOString() : null
        };
      })
    };

    markAttendanceMutation.mutate(attendanceData);
  };

  const openDeleteConfirmation = async (record) => {
    setRecordPendingDeletion(record);
    setShowDeleteConfirmModal(true);

    // If records are empty, fetch them in the background to update the count
    if (!record.records || record.records.length === 0) {
      try {
        const sessionId = record.sessionId || record.id || record._id;
        const result = await trainingService.getAttendance({ sessionId, limit: 1000 });
        if (result.success) {
          const rawRecords = result.data?.attendanceRecords || result.data?.data || (Array.isArray(result.data) ? result.data : []);
          const formatted = rawRecords.map((rec, idx) => formatAttendanceParticipant(rec, idx));
          setRecordPendingDeletion(prev => (prev && (prev.id === record.id || prev.sessionId === record.sessionId)) ? { ...prev, records: formatted } : prev);
        }
      } catch (e) {
      }
    }
  };

  const closeDeleteConfirmation = () => {
    setShowDeleteConfirmModal(false);
    setRecordPendingDeletion(null);
  };

  // Delete attendance mutation
  const deleteAttendanceMutation = useMutation({
    mutationFn: async (records) => {
      if (!records || records.length === 0) return [];
      let errors = [];
      for (const record of records) {
        const attendanceId = record.attendanceId || record._id || record.id;
        if (!attendanceId || String(attendanceId).includes('participant-')) continue;
        const res = await trainingService.deleteAttendance(attendanceId);
        if (!res.success) errors.push(res.error || 'Failed to delete record');
      }
      if (records.length > 0 && errors.length === records.length) throw new Error(errors[0]);
      return errors;
    },
    onSuccess: (errors) => {
      showSuccess('Attendance records deleted successfully.');
      if (errors.length > 0) showWarning(`Some records could not be deleted.`);
      queryClient.invalidateQueries(['attendance']);
      closeDeleteConfirmation();
    },
    onError: (err) => {
      showError(err.message || 'Failed to delete attendance records');
      closeDeleteConfirmation();
    }
  });

  const handleDeleteSession = async () => {
    if (!recordPendingDeletion) return;

    setIsDeletingAttendance(true);
    try {
      const sessionId = recordPendingDeletion.sessionId || recordPendingDeletion.id || recordPendingDeletion._id;
      const result = await trainingService.deleteSession(sessionId);
      if (result.success) {
        showSuccess('Session and its attendance records deleted successfully.');
        queryClient.invalidateQueries(['sessions']);
        queryClient.invalidateQueries(['attendance']);
        closeDeleteConfirmation();
      } else {
        showError(result.error || 'Failed to delete session');
      }
    } catch (e) {
      showError('An error occurred while deleting the session');
    } finally {
      setIsDeletingAttendance(false);
    }
  };

  const queryErrorMsg = sessionsError?.message || queryError?.message || (isSessionsError ? 'Failed to load sessions' : null);
  const isUpdatingAttendance = markAttendanceMutation.isLoading;
  const isMutationLoading = isUpdatingAttendance || isDeletingAttendance;
  const isComponentLoading = isSessionsLoading || isLoading || isMutationLoading;
  const error = queryErrorMsg;

  // Bulk mark attendance for all participants
  const handleBulkMarkAttendance = (status) => {
    const newStatus = { ...attendanceStatus };
    participants.forEach(person => {
      newStatus[person.id] = status;
    });
    setAttendanceStatus(newStatus);
  };

  // Clear all attendance
  const handleClearAttendance = () => {
    if (window.confirm('Are you sure you want to clear all attendance marks?')) {
      setAttendanceStatus({});
      setAttendanceNotes({});
    }
  };

  // Handle CSV import
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      // Process CSV file here
    }
  };

  const getRecordDateDisplay = (record) => {
    const dateSource =
      record.date ||
      record.records?.[0]?.checkInTime ||
      record.records?.[0]?.createdAt ||
      null;

    if (!dateSource) return 'N/A';
    try {
      return new Date(dateSource).toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const getAttendanceCounts = (record) => {
    const attendanceObj = record.attendance || {};
    const values = Object.values(attendanceObj);
    return {
      present: values.filter((status) => status === 'present').length,
      absent: values.filter((status) => status === 'absent').length,
      late: values.filter((status) => status === 'late').length
    };
  };

  // Row selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Map(selectedRows);
      (Array.isArray(attendanceData) ? attendanceData : []).forEach(row => {
        if (row.id) newSelected.set(row.id, row);
      });
      setSelectedRows(newSelected);
    } else {
      const newSelected = new Map(selectedRows);
      (Array.isArray(attendanceData) ? attendanceData : []).forEach(row => {
        if (row.id) newSelected.delete(row.id);
      });
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (row) => {
    const newSelected = new Map(selectedRows);
    if (newSelected.has(row.id)) {
      newSelected.delete(row.id);
    } else {
      newSelected.set(row.id, row);
    }
    setSelectedRows(newSelected);
  };

  // --- Bulk Delete Handlers ---
  const handleBulkDelete = () => {
    if (selectedRows.size === 0) {
      showError('Please select at least one session to delete');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    const sessionIds = Array.from(selectedRows.keys());
    let successCount = 0;
    let errorCount = 0;

    for (const sessionId of sessionIds) {
      try {
        const result = await trainingService.deleteSession(sessionId);
        if (result && result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        errorCount++;
      }
    }

    setIsBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedRows(new Map());
    queryClient.invalidateQueries(['sessions']);
    queryClient.invalidateQueries(['attendance']);

    if (successCount > 0) showSuccess(`${successCount} session(s) deleted successfully`);
    if (errorCount > 0) showError(`Failed to delete ${errorCount} session(s)`);
  };





  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) setShowInlineSelectedFormats(false);
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (selectedExportWrapRef.current && !selectedExportWrapRef.current.contains(e.target)) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleExportCurrentPage = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({ scope: 'currentPage', format });
      showSuccess('Current page export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportFiltered = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({ scope: 'filtered', format });
      showSuccess('Filtered export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportRange = async (startDate, endDate, format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'dateRange',
        format,
        overrideFilters: { ...baseExportFilters, startDate, endDate }
      });
      showSuccess('Date range export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportAll = async (format) => {
    setExportLoading(true);
    try {
      // Pass empty filters so "Export All" ignores any active search/date filters
      await requestBackendExport({ scope: 'all', format, overrideFilters: {} });
      showSuccess('All data export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportSelected = async (format) => {
    setExportLoading(true);
    try {
      const selectedIds = Array.from(selectedRows.keys());
      await requestBackendExport({ scope: 'selected', format, selectedIds });
      showSuccess('Selected rows export successful');
      setSelectedRows(new Map());
      setShowInlineSelectedFormats(false);
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
    }
  };


  // Clear all filters and search
  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to first page when changing limit
  };


  // Utility functions
  const convertToCSV = (data) => {
    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');
    return csvContent;
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusMeta = (status) => {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'present':
        return {
          label: 'Present',
          className: 'bg-green-100 text-green-700 border border-green-200',
          icon: <CheckCircle className="h-4 w-4 text-green-600" />
        };
      case 'absent':
        return {
          label: 'Absent',
          className: 'bg-red-100 text-red-700 border border-red-200',
          icon: <XCircle className="h-4 w-4 text-red-600" />
        };
      case 'late':
        return {
          label: 'Late',
          className: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
          icon: <Clock className="h-4 w-4 text-yellow-600" />
        };
      default:
        return {
          label: 'Not Marked',
          className: 'bg-gray-100 text-gray-700 border border-gray-200',
          icon: <AlertCircle className="h-4 w-4 text-gray-500" />
        };
    }
  };

  const statusToggleOptions = [
    {
      value: 'present',
      label: 'Present',
      activeClass: 'bg-agrosaf-green text-white border-agrosaf-green',
      inactiveClass: 'bg-white text-gray-600 border-gray-300'
    },
    {
      value: 'absent',
      label: 'Absent',
      activeClass: 'bg-red-600 text-white border-red-600',
      inactiveClass: 'bg-white text-gray-600 border-gray-300'
    },
    {
      value: 'late',
      label: 'Late',
      activeClass: 'bg-yellow-500 text-white border-yellow-500',
      inactiveClass: 'bg-white text-gray-600 border-gray-300'
    }
  ];

  const renderStatusButtons = (participantId) => {
    const currentStatus = attendanceStatus[participantId] ?? null;
    return (
      <div className="flex flex-wrap gap-2">
        {statusToggleOptions.map(option => {
          const isActive = currentStatus === option.value;
          return (
            <button
              key={`${participantId}-${option.value}`}
              type="button"
              onClick={() => handleAttendanceChange(participantId, option.value)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${isActive ? option.activeClass : option.inactiveClass
                }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="px-6 py-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Attendance Management</h1>
            <p className="text-gray-600 mt-2">Manage sessions attendance</p>
          </div>
          <div className="flex items-center space-x-4">
            {selectedRows.size > 0 && (
              <PermissionGate permission="training" action="delete">
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedRows.size})
                </button>
              </PermissionGate>
            )}
            <PermissionGate permission="training" action="read">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant={selectedRows.size > 0 ? "primary" : "outline"}
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                  loading={exportLoading}
                >
                  {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleExportSelected}
                />
              </div>
            </PermissionGate>
            <PermissionGate permission="training" action="create">
              <button
                onClick={() => {
                  clearAttendanceFormState();
                  setIsAttendanceFormOpen(true);
                  setIsEditingAttendance(false);
                }}
                className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Mark Attendance
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      <div className="space-y-6">
          {isAttendanceFormOpen ? (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isEditingAttendance ? 'Edit Attendance' : 'Mark Attendance'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedSession
                      ? `${participants.length} participant${participants.length === 1 ? '' : 's'} loaded`
                      : 'Select a session to load participants'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleCancelAttendanceForm}
                >
                  Close
                </Button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Session
                  </label>
                  <select
                    value={selectedSession}
                    onChange={(e) => setSelectedSession(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  >
                    <option value="">Choose a session...</option>
                    {dropdownSessions.map(session => {
                      const hasAttendance = attendanceData.some(record => (record.sessionId || '') === (session.id || session._id || ''));
                      return (
                        <option key={session.id || session._id} value={session.id || session._id}>
                          {session.title} - {session.scheduledDate ? new Date(session.scheduledDate).toLocaleDateString() : 'N/A'} {session.startTime ? new Date(session.startTime).toLocaleTimeString() : 'N/A'}
                          {hasAttendance ? ' (Attendance Exists)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {selectedSession && attendanceData.some(record => (record.sessionId || '') === selectedSession) && (
                    <p className="mt-2 text-sm text-blue-600">
                      ℹ️ Attendance already exists for this session. Updating will modify existing records.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="border border-gray-200 rounded-lg">
                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Users className="h-4 w-4 text-agrosaf-green" />
                        Trainers ({trainers.length})
                      </h4>
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                      {trainers.length === 0 && (
                        <div className="p-4 text-sm text-gray-500">No trainers assigned to this session</div>
                      )}
                      {[...trainers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(trainer => {
                        const status = attendanceStatus[trainer.id];
                        return (
                          <div key={trainer.id} className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900 truncate max-w-[220px]" title={trainer.name}>
                                  {trainer.name}
                                </p>
                                {trainer.email && (
                                  <p className="text-sm text-gray-500">{trainer.email}</p>
                                )}
                              </div>
                              {(() => {
                                const statusMeta = getStatusMeta(status);
                                return (
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${statusMeta.className}`}>
                                    {statusMeta.icon}
                                    <span>{statusMeta.label}</span>
                                  </span>
                                );
                              })()}
                            </div>
                            {renderStatusButtons(trainer.id)}
                            {!trainer.userId && (
                              <p className="text-xs text-red-500">
                                No linked user account. Please update this trainer&apos;s profile before saving attendance.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg">
                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-500" />
                        Invitees ({invitees.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        <select
                          value={formSortConfig.key}
                          onChange={(e) => handleFormSort(e.target.value)}
                          className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white"
                        >
                          <option value="name">Sort by Name</option>
                          <option value="department">Sort by Dept</option>
                        </select>
                        <div className="relative">
                          <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search invitees..."
                            value={inviteeSearchTerm}
                            onChange={(e) => setInviteeSearchTerm(e.target.value)}
                            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                      {invitees.length === 0 && (
                        <div className="p-4 text-sm text-gray-500">No invitees enrolled for this session</div>
                      )}
                      {invitees.length > 0 && sortedInvitees.length === 0 && (
                        <div className="p-4 text-sm text-gray-500">No invitees match your search</div>
                      )}
                      {sortedInvitees.map(invitee => {
                        const status = attendanceStatus[invitee.id];
                        return (
                          <div key={invitee.id} className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900 truncate max-w-[220px]" title={invitee.name}>
                                  {invitee.name}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {[invitee.department, invitee.email].filter(Boolean).join(' • ') || 'No additional info'}
                                </p>
                              </div>
                              {(() => {
                                const statusMeta = getStatusMeta(status);
                                return (
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${statusMeta.className}`}>
                                    {statusMeta.icon}
                                    <span>{statusMeta.label}</span>
                                  </span>
                                );
                              })()}
                            </div>
                            {renderStatusButtons(invitee.id)}
                            {!invitee.userId && (
                              <p className="text-xs text-red-500">
                                No linked user account. Please update this participant before saving attendance.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-agrosaf-green" />
                      Quick Actions
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleBulkMarkAttendance('present')}
                        className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <CheckCircle className="h-4 w-4 mr-1.5" />
                        Mark All Present
                      </button>
                      <button
                        onClick={() => handleBulkMarkAttendance('absent')}
                        className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <XCircle className="h-4 w-4 mr-1.5" />
                        Mark All Absent
                      </button>
                      <button
                        onClick={() => handleBulkMarkAttendance('late')}
                        className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors"
                      >
                        <Clock className="h-4 w-4 mr-1.5" />
                        Mark All Late
                      </button>
                      <button
                        onClick={handleClearAttendance}
                        className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <RefreshCw className="h-4 w-4 mr-1.5" />
                        Clear All
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    Participants start without a status. Use the buttons above or each participant&apos;s toggles to set their attendance.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={handleCancelAttendanceForm}>
                    Cancel
                  </Button>
                  <button
                    onClick={isEditingAttendance ? handleUpdateAttendance : handleSaveAttendance}
                    disabled={!selectedSession || isComponentLoading || participants.length === 0}
                    className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isComponentLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {isEditingAttendance ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isEditingAttendance ? 'Update Attendance' : 'Save Attendance'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex flex-wrap items-end gap-4 w-full">
                  <div className="relative flex-[2] min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by Session..."
                      value={searchTerm}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                        setSearchTerm(value);
                      }}
                      onKeyPress={(e) => {
                        if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      maxLength={100}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          const input = document.querySelector('input[placeholder="Search participants..."]');
                          if (input) input.focus();
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex-shrink-0 relative z-10 w-auto">
                    <DateRangeFilter
                      startDate={dateRange.startDate}
                      endDate={dateRange.endDate}
                      onChange={(range) => {
                        setDateRange(range);
                        setCurrentPage(1);
                      }}
                    />
                  </div>

                  {(searchTerm || dateRange.startDate || dateRange.endDate) && (
                    <div className="relative flex-shrink-0">
                      <Button
                        variant="outline"
                        icon={<X className="h-4 w-4" />}
                        onClick={handleClearFilters}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Attendance Records */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Attendance Records</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                            checked={attendanceData.length > 0 && attendanceData.every(row => selectedRows.has(row.id))}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('sessionTitle')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Session</span>
                            {sortConfig.key === 'sessionTitle' ? (
                              sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('date')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Date</span>
                            {sortConfig.key === 'date' ? (
                              sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('present')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Present</span>
                            {sortConfig.key === 'present' ? (
                              sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('absent')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Absent</span>
                            {sortConfig.key === 'absent' ? (
                              sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('late')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Late</span>
                            {sortConfig.key === 'late' ? (
                              sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(Array.isArray(sortedAttendanceData) ? sortedAttendanceData : []).length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No attendance records found</p>
                            <p className="text-sm">Click "Mark Attendance" to create your first record</p>
                          </td>
                        </tr>
                      ) : (
                        (Array.isArray(sortedAttendanceData) ? sortedAttendanceData : []).map((record, index) => {
                          const session = paginatedSessions.find(s => (s.id || s._id) === record.sessionId);
                          const attendanceObj = record.attendance || {};

                          // Handle both detailed status map and summary counts
                          let presentCount = 0;
                          let absentCount = 0;
                          let lateCount = 0;

                          if (attendanceObj.present !== undefined) {
                            // Using backend summary counts
                            presentCount = attendanceObj.present || 0;
                            absentCount = attendanceObj.absent || 0;
                            lateCount = attendanceObj.late || 0;
                          } else {
                            // Fallback to detailed map count
                            presentCount = Object.values(attendanceObj).filter(status => status === 'present').length;
                            absentCount = Object.values(attendanceObj).filter(status => status === 'absent').length;
                            lateCount = Object.values(attendanceObj).filter(status => status === 'late').length;
                          }

                          // Get session title and truncate it
                          const sessionTitle = record.sessionTitle || session?.title || 'Unknown Session';
                          const truncatedTitle = sessionTitle.length > 50 ? `${sessionTitle.substring(0, 50)}...` : sessionTitle;

                          return (
                            <tr key={record.id || index}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                                  checked={selectedRows.has(record.id)}
                                  onChange={() => handleSelectRow(record)}
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={sessionTitle}>
                                  {truncatedTitle}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {(() => {
                                  const dateSource =
                                    record.date ||
                                    record.records?.[0]?.checkInTime ||
                                    record.records?.[0]?.createdAt ||
                                    null;
                                  return dateSource
                                    ? formatDate(dateSource, { includeTime: false })
                                    : 'N/A';
                                })()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {presentCount}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  {absentCount}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {lateCount}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <PermissionGate permission="training" action="read">
                                    <button
                                      onClick={() => handleViewAttendance(record)}
                                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="View Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                  <PermissionGate permission="training" action="update">
                                    <button
                                      onClick={() => handleEditAttendance(record)}
                                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Edit Attendance"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                  <PermissionGate permission="training" action="delete">
                                    <button
                                      onClick={() => openDeleteConfirmation(record)}
                                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete Session"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {!isComponentLoading && pagination && pagination.totalPages > 1 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                    {/* Results Count and Limit Selector */}
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-700">
                        {(() => {
                          const total = pagination.totalRecords;
                          if (total === 0) {
                            return 'Showing 0 to 0 of 0 results';
                          }
                          const start = ((pagination.currentPage || 1) - 1) * (pagination.limit || 10) + 1;
                          const end = Math.min((pagination.currentPage || 1) * (pagination.limit || 10), total);
                          return `Showing ${start} to ${end} of ${total} results`;
                        })()}
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Items per page:</label>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                          className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={200}>200</option>
                        </select>
                      </div>
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center space-x-2">
                      <PermissionGate permission="training" action="read">
                        <button
                          onClick={() => handlePageChange((pagination.currentPage || 1) - 1)}
                          disabled={pagination.hasPrevPage === false || (pagination.currentPage || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>
                      </PermissionGate>

                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {(() => {
                          const totalPages = pagination.totalPages || 1;
                          const currentPageNum = pagination.currentPage || 1;
                          const pages = [];

                          if (totalPages <= 10) {
                            // Show all pages if 10 or fewer
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // Always show first page
                            pages.push(1);

                            if (currentPageNum <= 4) {
                              // Show pages 2-5, then ellipsis, then last page
                              for (let i = 2; i <= 5; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            } else if (currentPageNum >= totalPages - 3) {
                              // Show first page, ellipsis, then last 5 pages
                              pages.push('ellipsis');
                              for (let i = totalPages - 4; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              // Show first page, ellipsis, current-1, current, current+1, ellipsis, last page
                              pages.push('ellipsis');
                              for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            }
                          }

                          return pages.map((page, index) => {
                            if (page === 'ellipsis') {
                              return (
                                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                  ...
                                </span>
                              );
                            }

                            return (
                              <PermissionGate permission="training" action="read" key={page}>
                                <button
                                  onClick={() => handlePageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-agrosaf-green text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              </PermissionGate>
                            );
                          });
                        })()}
                      </div>

                      <PermissionGate permission="training" action="read">
                        <button
                          onClick={() => handlePageChange((pagination.currentPage || 1) + 1)}
                          disabled={pagination.hasNextPage === false || (pagination.currentPage || 1) >= (pagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </PermissionGate>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      {
        showDeleteConfirmModal && recordPendingDeletion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Delete Session</h3>
                <button
                  onClick={closeDeleteConfirmation}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-700 break-words">
                  Are you sure you want to delete session{' '}
                  <span className="font-semibold break-words">
                    {recordPendingDeletion.sessionTitle ||
                      dropdownSessions.find(s => (s.id || s._id) === recordPendingDeletion.sessionId)?.title ||
                      'this session'}
                  </span>
                  ?
                </p>
                <p className="text-sm text-gray-500 break-words">
                  This action will remove the session and all its attendance records. This cannot be undone.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={closeDeleteConfirmation}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isDeletingAttendance}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSession}
                  disabled={isDeletingAttendance}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isDeletingAttendance ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Session
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }


      {/* View Attendance Details Modal */}
      {
        showViewAttendanceModal && selectedAttendanceRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Attendance Details</h3>
                  <button
                    onClick={() => setShowViewAttendanceModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                {isViewAttendanceLoading ? (
                  <div className="py-10 text-center text-gray-500 space-y-3">
                    <RefreshCw className="h-8 w-8 mx-auto animate-spin text-agrosaf-green" />
                    <p>Loading latest attendance details...</p>
                  </div>
                ) : (
                  (() => {
                    const session = dropdownSessions.find(s => (s.id || s._id) === selectedAttendanceRecord.sessionId);
                    const fallbackParticipants = (selectedAttendanceRecord.records || []).map((record, idx) =>
                      formatAttendanceParticipant(record, idx)
                    );
                    const baseParticipants = viewAttendanceDetails?.participants || fallbackParticipants;
                    const participants = [...baseParticipants].sort((a, b) => {
                      let aValue, bValue;
                      if (modalSortConfig.key === 'name') {
                        aValue = (a.name || '').toLowerCase();
                        bValue = (b.name || '').toLowerCase();
                      } else if (modalSortConfig.key === 'status') {
                        aValue = a.status || '';
                        bValue = b.status || '';
                      } else if (modalSortConfig.key === 'department') {
                        aValue = (a.department || '').toLowerCase();
                        bValue = (b.department || '').toLowerCase();
                      } else {
                        aValue = a[modalSortConfig.key] || '';
                        bValue = b[modalSortConfig.key] || '';
                      }
                      if (aValue < bValue) return modalSortConfig.direction === 'asc' ? -1 : 1;
                      if (aValue > bValue) return modalSortConfig.direction === 'asc' ? 1 : -1;
                      return 0;
                    });
                    const presentCount =
                      viewAttendanceDetails?.counts?.present ??
                      participants.filter(p => p.status === 'present').length;
                    const absentCount =
                      viewAttendanceDetails?.counts?.absent ??
                      participants.filter(p => p.status === 'absent').length;
                    const lateCount =
                      viewAttendanceDetails?.counts?.late ??
                      participants.filter(p => p.status === 'late').length;
                    const detailSessionTitle =
                      viewAttendanceDetails?.sessionTitle ||
                      selectedAttendanceRecord.sessionTitle ||
                      session?.title ||
                      'Unknown Session';
                    const detailDate =
                      viewAttendanceDetails?.date ||
                      selectedAttendanceRecord.date ||
                      selectedAttendanceRecord.records?.[0]?.checkInTime ||
                      selectedAttendanceRecord.records?.[0]?.createdAt ||
                      null;

                    return (
                      <div className="space-y-6">
                        {viewAttendanceError && (
                          <div className="p-3 rounded-lg bg-red-50 text-sm text-red-600">
                            {viewAttendanceError}
                          </div>
                        )}
                        {/* Session Info */}
                        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                          <div>
                            <p className="text-sm text-gray-500">Session Title</p>
                            <p className="font-medium text-gray-900 break-words">{detailSessionTitle}</p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-500">Date</p>
                              <p className="font-medium">
                                {detailDate ? new Date(detailDate).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Participants</p>
                              <p className="font-medium">{participants.length}</p>
                            </div>
                          </div>
                        </div>

                        {/* Attendance Summary */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="bg-green-50 p-4 rounded-lg text-center">
                            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-green-600">
                              {presentCount}
                            </p>
                            <p className="text-sm text-green-600">Present</p>
                          </div>
                          <div className="bg-red-50 p-4 rounded-lg text-center">
                            <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-red-600">
                              {absentCount}
                            </p>
                            <p className="text-sm text-red-600">Absent</p>
                          </div>
                          <div className="bg-yellow-50 p-4 rounded-lg text-center">
                            <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-yellow-600">
                              {lateCount}
                            </p>
                            <p className="text-sm text-yellow-600">Late</p>
                          </div>
                        </div>

                        {/* Participant Details */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-gray-900">Participant Details</h4>
                          </div>
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th
                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                                    onClick={() => handleModalSort('name')}
                                  >
                                    <div className="flex items-center space-x-1">
                                      <span>Name</span>
                                      {modalSortConfig.key === 'name' ? (
                                        modalSortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                                      ) : (
                                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                                      )}
                                    </div>
                                  </th>
                                  <th
                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                                    onClick={() => handleModalSort('department')}
                                  >
                                    <div className="flex items-center space-x-1">
                                      <span>Dept</span>
                                      {modalSortConfig.key === 'department' ? (
                                        modalSortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                                      ) : (
                                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                                      )}
                                    </div>
                                  </th>
                                  <th
                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                                    onClick={() => handleModalSort('status')}
                                  >
                                    <div className="flex items-center space-x-1">
                                      <span>Status</span>
                                      {modalSortConfig.key === 'status' ? (
                                        modalSortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                                      ) : (
                                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                                      )}
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {participants.length > 0 ? (
                                  participants.map(participant => (
                                    <tr key={participant.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center space-x-3">
                                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                            <User className="h-4 w-4 text-gray-600" />
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]" title={participant.name}>
                                              {participant.name || 'Unknown'}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate max-w-[150px]" title={participant.email}>
                                              {participant.email}
                                            </p>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <p className="text-xs text-gray-500 truncate max-w-[100px]" title={participant.department}>
                                          {participant.department || 'N/A'}
                                        </p>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex flex-col">
                                          {(() => {
                                            const status = (participant.status || 'present').toLowerCase();
                                            const statusMap = {
                                              present: { label: 'Present', color: 'bg-green-100 text-green-800' },
                                              absent: { label: 'Absent', color: 'bg-red-100 text-red-800' },
                                              late: { label: 'Late', color: 'bg-yellow-100 text-yellow-800' }
                                            };
                                            const statusMeta = statusMap[status] || statusMap.present;
                                            return (
                                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusMeta.color}`}>
                                                {statusMeta.label}
                                              </span>
                                            );
                                          })()}
                                          {participant.notes && (
                                            <p className="text-[10px] text-gray-400 mt-1 truncate max-w-[100px]" title={participant.notes}>
                                              {participant.notes}
                                            </p>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="3" className="px-4 py-6 text-center text-gray-500 text-sm">
                                      No participant details available
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowViewAttendanceModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Import CSV Modal */}
      {
        showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Import Attendance CSV</h3>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select CSV File
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileImport}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    />
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>CSV Format:</strong> Employee ID, Name, Status (Present/Absent/Late), Notes
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    alert('CSV imported successfully!');
                  }}
                  disabled={!importFile}
                  className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Import Data
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Sessions</h3>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-red-600">{selectedRows.size}</span>{' '}
              selected session{selectedRows.size !== 1 ? 's' : ''} and their attendance records?
            </p>
            <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isBulkDeleting ? (
                  <>
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Sessions ({selectedRows.size})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Attendance"
        scopedFormatExport={{
          onExport: (scope, format, meta) => {
            if (scope === 'currentPage') return handleExportCurrentPage(format);
            if (scope === 'filtered') return handleExportFiltered(format);
            if (scope === 'dateRange') return handleExportRange(meta.startDate, meta.endDate, format);
            if (scope === 'all') return handleExportAll(format);
          }
        }}
      />
    </div>
  );
};

export default AttendanceFeedback;
